import { useState } from 'react';
import CalEmbed from '../components/CalEmbed';
import { INTRO_CAL_LINK } from '../lib/qualification';
import { useDocumentMeta } from '../lib/useDocumentMeta';

type Step = 'form' | 'calendar';

/**
 * The introductions booking route. Deliberately separate from /book (the AI
 * diagnostic funnel): no budget or revenue questions, no qualification, and no
 * not-a-fit message. It collects only name, email and firm name, then hands the
 * visitor straight to the 20-minute intro call. Firm name rides along in the
 * booking notes since the Cal event has no native firm field.
 */
export default function IntroductionsBook() {
  useDocumentMeta({
    title: 'Book an introduction — Samia Systems',
    description:
      'A 20 minute conversation to understand what you are strong at and what is worth your team’s time.',
  });

  const [step, setStep] = useState<Step>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [firm, setFirm] = useState('');

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setStep('calendar');
  };

  return (
    <>
      <section className="grain relative bg-black px-6 pb-12 pt-40">
        <div className="container-x">
          <p className="label mb-6 inline-flex items-center gap-3">
            <span className="h-px w-10 bg-gold" />
            Introductions
          </p>
          <h1 className="max-w-4xl font-display text-hero font-light text-white text-balance">
            A 20 minute{' '}
            <span className="italic text-gold">conversation</span>.
          </h1>
          <p className="mt-8 max-w-2xl font-sans text-base text-white/85">
            We want to understand what you are strong at and what is worth your
            team&apos;s time. By the end we should both know whether there is a
            fit.
          </p>
        </div>
      </section>

      <section className="bg-black pb-32">
        <div className="container-x">
          <div className="mx-auto max-w-4xl">
            {step === 'form' && (
              <div className="grain rounded-5xl border border-white/10 bg-charcoal p-6 md:p-10">
                <h2 className="font-display text-2xl font-light text-white">
                  A few details, then pick a time
                </h2>
                <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-5">
                  <div className="grid gap-5 md:grid-cols-2">
                    <Field
                      label="Your name"
                      value={name}
                      onChange={setName}
                      required
                      autoComplete="name"
                    />
                    <Field
                      label="Email address"
                      type="email"
                      value={email}
                      onChange={setEmail}
                      required
                      autoComplete="email"
                    />
                  </div>

                  <Field
                    label="Firm name"
                    value={firm}
                    onChange={setFirm}
                    required
                    autoComplete="organization"
                  />

                  <button
                    type="submit"
                    className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-gold bg-gold px-7 py-3.5 font-sans text-[0.7rem] uppercase tracking-label text-black transition-opacity hover:opacity-90"
                  >
                    Choose a time →
                  </button>
                </form>
              </div>
            )}

            {step === 'calendar' && (
              <CalEmbed
                calLink={INTRO_CAL_LINK}
                prefill={{
                  name,
                  email,
                  notes: `Firm: ${firm}`,
                  guests: [],
                }}
              />
            )}
          </div>

          {step === 'calendar' && (
            <p className="mt-8 text-center font-mono text-[0.65rem] uppercase tracking-label text-stone">
              20 minutes · Cal Video
            </p>
          )}
        </div>
      </section>
    </>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  required,
  autoComplete,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="label mb-2 block">
        {label}
        {required ? ' *' : ''}
        <input
          type={type}
          value={value}
          required={required}
          autoComplete={autoComplete}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className="mt-2 w-full rounded-2xl border border-white/10 bg-black/60 px-4 py-3 font-sans text-sm normal-case tracking-normal text-white placeholder-stone focus:border-gold focus:outline-none"
        />
      </label>
    </div>
  );
}
